import {
  BRANCH_NO_OFFSET,
  BRANCH_YES_OFFSET,
  DEFAULT_START_X,
  DEFAULT_START_Y,
  PRIMITIVE_GAP,
} from "../../types/editor.constants";
import { Step } from "../../types/TransactionTemplate.types";
import {
  findMaxXOnRow,
  getPrimWidth,
} from "../../utils/transactions/editor.utils";

interface AddMode {
  afterPrimitive: { stepIndex: number; primIndex: number };
  branch?: "yes" | "no" | "after";
}

interface PlacementResult {
  startX: number;
  startY: number;
  connectionSourceId: string | null;
  isBranching: boolean;
  branchType?: "yes" | "no" | "after";
}

export function computePlacement(
  step: Step,
  addMode: AddMode | null,
  selectedPrimitive: { stepIndex: number; primIndex: number } | null,
  targetStepIndex: number,
): PlacementResult {
  let startX = 200;
  let startY = 70;
  let connectionSourceId: string | null = null;
  let isBranching = false;
  let branchType: "yes" | "no" | "after" | undefined;

  if (addMode) {
    const { afterPrimitive, branch } = addMode;
    branchType = branch;

    if (afterPrimitive.primIndex === -1 && step.event) {
      connectionSourceId = step.event.id;
      startX = DEFAULT_START_X;
      startY = DEFAULT_START_Y;
    } else if (afterPrimitive.primIndex >= 0) {
      const source = step.primitives[afterPrimitive.primIndex];
      if (source) {
        connectionSourceId = source.id;
        isBranching = true;

        if (branch === "after") {
          startX = source.x + PRIMITIVE_GAP;
          startY = source.y;
        } else if (branch === "yes" || branch === "no") {
          const yOffset =
            branch === "yes" ? BRANCH_YES_OFFSET : BRANCH_NO_OFFSET;
          startY = source.y + yOffset;
          const rowMax = findMaxXOnRow(step.primitives, startY);
          startX =
            rowMax > 0 ? rowMax + PRIMITIVE_GAP : source.x + PRIMITIVE_GAP;
        }
      }
    }
  } else if (step.primitives.length === 0 && step.event) {
    connectionSourceId = step.event.id;
    startX = DEFAULT_START_X;
    startY = DEFAULT_START_Y;
  } else if (selectedPrimitive?.primIndex === -1 && step.event) {
    connectionSourceId = step.event.id;
    startX = DEFAULT_START_X;
    startY = DEFAULT_START_Y;
  } else if (
    selectedPrimitive &&
    selectedPrimitive.primIndex >= 0 &&
    selectedPrimitive.stepIndex === targetStepIndex
  ) {
    const source = step.primitives[selectedPrimitive.primIndex];
    if (source) {
      connectionSourceId = source.id;
      startX = findMaxXOnRow(step.primitives, source.y) + PRIMITIVE_GAP;
      startY = source.y;
    }
  } else if (step.primitives.length > 0) {
    const last = step.primitives[step.primitives.length - 1];
    connectionSourceId = last.id;
    startX = last.x + PRIMITIVE_GAP;
    startY = last.y;
  }

  // console.log("placement:", { startX, startY });

  return { startX, startY, connectionSourceId, isBranching, branchType };
}

export function resolveBranchConnection(
  step: Step,
  connectionSourceId: string,
  isBranching: boolean,
  branchType: string | undefined,
  startY: number,
  newPrimId: string,
): string {
  if (!isBranching || (branchType !== "yes" && branchType !== "no")) {
    return connectionSourceId;
  }

  const branchPrimitives = step.primitives.filter(
    (p) => Math.abs(p.y - startY) < 30 && p.id !== newPrimId,
  );

  if (branchPrimitives.length > 0) {
    return branchPrimitives.reduce((max, p) => (p.x > max.x ? p : max)).id;
  }

  return connectionSourceId;
}
